import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { BRAND } from "../../lib/brand";

const nav = [
  { label: "Soins", href: "#soins" },
  { label: "Signature", href: "#signature" },
  { label: "Témoignages", href: "#temoignages" },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-cream/85 backdrop-blur-md border-b border-olive/10"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-10 flex items-center justify-between h-[72px] md:h-[88px]">
        <a
          href="#top"
          className="flex items-baseline gap-2 group"
          data-testid="logo-home"
        >
          <span className="font-serif text-2xl md:text-[28px] tracking-tight text-olive">
            Corps
          </span>
          <span className="font-serif italic text-gold text-xl md:text-[22px] -mx-0.5">
            &amp;
          </span>
          <span className="font-serif text-2xl md:text-[28px] tracking-tight text-olive">
            Sens
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {nav.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="link-underline text-[13px] uppercase tracking-[0.22em] text-olive/85 hover:text-olive transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={BRAND.phoneHref}
            data-testid="cta-header-reserve"
            className="btn-gold hidden md:inline-flex items-center gap-2 px-6 py-3 text-[12px] uppercase tracking-[0.24em] rounded-full font-medium"
          >
            <Phone className="h-3.5 w-3.5" strokeWidth={1.6} />
            Réserver ma séance
          </a>
          <button
            aria-label="Ouvrir le menu"
            data-testid="nav-toggle"
            className="lg:hidden h-11 w-11 grid place-items-center rounded-full border border-olive/25 text-olive"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-cream border-t border-olive/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {nav.map((n, i) => (
                <motion.a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.05 * i, duration: 0.5 }}
                  className="font-serif text-3xl text-olive"
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </motion.a>
              ))}
              <a
                href={BRAND.phoneHref}
                data-testid="cta-mobile-menu-reserve"
                className="btn-gold mt-3 inline-flex items-center justify-center gap-2 py-4 text-[12px] uppercase tracking-[0.24em] rounded-full"
              >
                <Phone className="h-4 w-4" /> Réserver ma séance
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
